// src/app/components/PixBimViewer/Components/MouseHelpModal.tsx
import React from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  Divider,
  List,
  ThemeIcon,
  Box,
  Kbd,
  Badge,
} from "@mantine/core";
import {
  FaMousePointer,
  FaRegHandPaper,
  FaMouse,
  FaTrash,
  FaSyncAlt,
  FaInfoCircle,
} from "react-icons/fa";
import { MdOutlineMouse, MdOutlineTouchApp } from "react-icons/md";
import { FiMoreHorizontal } from "react-icons/fi";

export interface MouseHelpModalProps {
  opened: boolean;
  onClose: () => void;
}

export const MouseHelpModal: React.FC<MouseHelpModalProps> = ({ opened, onClose }) => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <FaMouse />
          <Text fw={700}>Musekontroller</Text>
          <Badge variant="light">Multiplacer Viewer</Badge>
        </Group>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        <Group gap="sm" align="flex-start">
          <ThemeIcon variant="light" size="lg">
            <FaInfoCircle />
          </ThemeIcon>

          <Box>
            <Text fw={600}>Kort forklart</Text>
            <Text size="sm" c="dimmed">
              Funksjoner kan variere litt avhengig av aktivt verktøy (f.eks. Måling / Drag-select)
              og hva du klikker på (rom vs. komponent).
            </Text>
          </Box>
        </Group>

        <Divider />

        {/* VENSTREKLIKK */}
        <Group gap="sm" align="flex-start">
          <ThemeIcon variant="light" size="lg">
            <FaMousePointer />
          </ThemeIcon>

          <Box>
            <Group gap="sm">
              <Text fw={700}>Venstreklikk</Text>
              <Badge variant="outline">Velg</Badge>
            </Group>

            <List size="sm" spacing="xs" mt={6} withPadding>
              <List.Item>Klikk på et rom for å velge det.</List.Item>
              <List.Item>
                Klikk på en komponent for å velge den og vise tilgjengelige handlinger.
              </List.Item>
              <List.Item>
                Hvis en komponent kan flyttes, kan du dra den etter et kort trykk (avhengig av
                konfigurasjon).
              </List.Item>
            </List>
          </Box>
        </Group>

        {/* HØYREKLIKK */}
        <Group gap="sm" align="flex-start">
          <ThemeIcon variant="light" size="lg">
            <FaMousePointer style={{ transform: "scaleX(-1)" }} />
          </ThemeIcon>

          <Box>
            <Group gap="sm">
              <Text fw={700}>Høyreklikk</Text>
              <Badge variant="outline">Kontekstmeny</Badge>
            </Group>

            <List size="sm" spacing="xs" mt={6} withPadding>
              <List.Item>
                Alternativene avhenger av hva du klikker på (for eksempel: <b>Slett</b>,{" "}
                <b>Roter</b>, <b>Erstatt</b>).
              </List.Item>
              <List.Item>Noen elementer har ikke kontekstmeny.</List.Item>
            </List>
          </Box>
        </Group>

        {/* MIDTKLIKK */}
        <Group gap="sm" align="flex-start">
          <ThemeIcon variant="light" size="lg">
            <FaRegHandPaper />
          </ThemeIcon>

          <Box>
            <Group gap="sm">
              <Text fw={700}>Midtknapp</Text>
              <Badge variant="outline">Panorer</Badge>
            </Group>

            <List size="sm" spacing="xs" mt={6} withPadding>
              <List.Item>Hold midtknappen inne og dra for å panorere.</List.Item>
              <List.Item>Bruk rullehjul for å zoome.</List.Item>
            </List>

            <Text size="sm" c="dimmed" mt="xs">
              Tips: På noen mus/trackpads må du aktivere «midtklikk» i systeminnstillingene.
            </Text>
          </Box>
        </Group>

        <Divider />

        {/* ALTERNATIV PAN (generisk) */}
        <Group gap="sm" align="flex-start">
          <ThemeIcon variant="light" size="lg">
            <FaMouse />
          </ThemeIcon>

          <Box>
            <Text fw={600}>Hvis du ikke har midtknapp</Text>
            <Text size="sm" c="dimmed">
              Noen oppsett støtter alternativ panorering (for eksempel en tast som holdes inne).
              Hvis panorering ikke fungerer, sjekk innstillinger i visningen eller få en utvikler
              til å aktivere en alternativ modus.
            </Text>

            <Group gap="xs" mt="xs">
              <Kbd>Space</Kbd>
              <Text size="sm" c="dimmed">
                + dra (valgfritt)
              </Text>
            </Group>
          </Box>
        </Group>
      </Stack>
    </Modal>
  );
};
